/**
 * Helpers de resolution d'URL d'asset sous /images/{kind}/.
 *
 * Convention de nommage alignee avec tools/ai_generate/generate.py:version_filename :
 *   - variant 0  -> `{code}.png`         (image principale, celle utilisee partout)
 *   - variant >= 1 -> `{code}_NN.png`    (variantes alternatives, NN = 2 digits)
 *
 * Le filesystem ne contient PAS de `{code}_00.png` : le "00" implicite est le
 * fichier sans suffixe. Cette convention permet aux assets simples (resources,
 * buildings, races, weather, ...) de partager le meme helper que les assets a
 * variantes (planets, characters, ...) sans avoir a tracker un index zero
 * superflu.
 */

/** Nom de fichier `{code}.png` ou `{code}_NN.png`. */
export function versionFilename(code: string, variant: number = 0): string {
  if (variant <= 0) return `${code}.png`;
  return `${code}_${variant.toString().padStart(2, '0')}.png`;
}

/** URL complete /images/{kind}/{filename}. */
export function assetUrl(kind: string, code: string, variant: number = 0): string {
  return `/images/${kind}/${versionFilename(code, variant)}`;
}

/**
 * Pour les codes legacy DB qui contiennent encore le suffixe `_00` ou `_NN`
 * (ex character_bodies pre-migration nommage), separe la base + variant.
 * `commandant_male_00` -> { baseCode: 'commandant_male', variant: 0 }
 * `commandant_male`    -> { baseCode: 'commandant_male', variant: 0 }
 * `commandant_male_02` -> { baseCode: 'commandant_male', variant: 2 }
 */
export function parseVariantCode(code: string): { baseCode: string; variant: number } {
  const m = code.match(/^(.+)_(\d{2})$/);
  if (!m) return { baseCode: code, variant: 0 };
  return { baseCode: m[1], variant: parseInt(m[2], 10) };
}

/**
 * Resout l'URL d'un asset depuis un code qui peut inclure (legacy) ou non un
 * suffixe `_NN`. Toujours sur le bon fichier selon la convention version_filename.
 */
export function assetUrlFromCode(kind: string, code: string): string {
  const { baseCode, variant } = parseVariantCode(code);
  return assetUrl(kind, baseCode, variant);
}

/**
 * Tailles autorisees par le backend (cf ImageThumbController.AllowedSizes).
 * Si on passe une valeur non listee on retombe sur 128 cote serveur, ce qui
 * est correct mais inefficace -- mieux vaut piocher ici.
 */
export type ThumbSize = 32 | 64 | 96 | 128 | 192 | 256;

/**
 * Variante miniature de assetUrl : route via /images/thumb/{size}/{kind}/{file}
 * qui resize a la volee (max side = size, keep aspect) avec cache disque.
 * A utiliser dans les divs petits (cards de batiments, vignettes de listes,
 * tooltips) ou l'image source 512x512 est gachee. Pour les hero / vue
 * detaillee, garder assetUrl direct.
 *
 * Le chemin URL match le path on-disk : nginx (cf deploy/nginx.conf,
 * location /images/thumb/) sert le cache directement apres le 1er hit,
 * Kestrel ne genere qu'au premier acces.
 *
 * NB: ne fonctionne QUE pour les .png (le controller refuse les autres
 * extensions). Tous les assets servis depuis /data/images sont en png.
 */
export function thumbUrl(kind: string, code: string, size: ThumbSize = 128, variant: number = 0): string {
  return `/images/thumb/${size}/${kind}/${versionFilename(code, variant)}`;
}

/** Variante de thumbUrl qui parse un code potentiellement suffixe _NN (cf assetUrlFromCode). */
export function thumbUrlFromCode(kind: string, code: string, size: ThumbSize = 128): string {
  const { baseCode, variant } = parseVariantCode(code);
  return thumbUrl(kind, baseCode, size, variant);
}

// ---------------------------------------------------------------------------
// Helpers par domaine (SOURCE UNIQUE — remplacent les fonctions shipImg/
// officerImg/buildingImg copiées dans de nombreux composants) + handler @error
// de repli pour les vaisseaux (chaîne variante race -> type neutre).
// ---------------------------------------------------------------------------

/** URL image d'un vaisseau : plein format, ou miniature si `size` fourni. null si code vide. */
export function shipImageUrl(code: string | null | undefined, size?: ThumbSize): string | null {
  if (!code) return null;
  return size ? thumbUrl('ships', code, size) : assetUrl('ships', code);
}

/** URL image d'un officier (plein format ou miniature). null si code vide. */
export function officerImageUrl(code: string | null | undefined, size?: ThumbSize): string | null {
  if (!code) return null;
  return size ? thumbUrl('officers', code, size) : assetUrl('officers', code);
}

/** URL miniature d'un bâtiment (défaut 192). null si code vide. */
export function buildingImageUrl(code: string | null | undefined, size: ThumbSize = 192): string | null {
  if (!code) return null;
  return thumbUrl('buildings', code, size);
}

const SHIP_RACE_SUFFIXES = ['humain', 'mutant', 'robot', 'alien', 'ancien'];

/**
 * Handler `@error` de repli pour une image de vaisseau, façon SOURCE UNIQUE
 * (était copié dans FleetPanel / FleetEditorModal / ShipList / ShipyardManagement) :
 *  1. variante de race `{type}_{race}` -> type neutre `{type}` ;
 *  2. -> `fallbackCode` (shipTypeCode) ;
 *  3. sinon masque l'image.
 * `size` : si fourni, repli en miniature (sinon plein format).
 */
export function onShipImgError(e: Event, code: string | null | undefined,
  fallbackCode?: string | null, size?: ThumbSize): void {
  const img = e.target as HTMLImageElement;
  const c = code ?? '';
  const i = c.lastIndexOf('_');
  const suffix = i >= 0 ? c.slice(i + 1) : '';
  if (i >= 0 && SHIP_RACE_SUFFIXES.includes(suffix) && !img.dataset.fb1) {
    img.dataset.fb1 = '1';
    img.src = shipImageUrl(c.slice(0, i), size)!;
    return;
  }
  if (fallbackCode && fallbackCode !== c && !img.dataset.fb2) {
    img.dataset.fb2 = '1';
    img.src = shipImageUrl(fallbackCode, size)!;
    return;
  }
  img.style.display = 'none';
}
